/**
 * Blog data — runtime localization layer.
 *
 * The raw, bilingual posts live in the AUTO-GENERATED `blog.generated.ts`
 * (built from content/blog/ by scripts/build-content.ts). This module collapses
 * them to the active language for the UI, mirroring the pattern used by
 * @/data/companyNews.
 */

import { getCurrentLanguage } from '@/utils/i18n'
import type { LocalizedBlogPost } from '@/types/blog'
import { blogPosts } from './blog.generated'

function localize(): LocalizedBlogPost[] {
  const lang = getCurrentLanguage()
  return blogPosts.map((post) => ({
    slug: post.slug,
    date: post.date,
    updated: post.updated,
    tags: post.tags,
    ogImage: post.ogImage,
    title: post.title[lang],
    seoTitle: post.seoTitle ? post.seoTitle[lang] : undefined,
    description: post.description[lang],
    html: post.html[lang],
    readingMinutes: post.readingMinutes[lang],
    faq: post.faq ? post.faq[lang] : [],
  }))
}

/** All published posts, newest-first, in the active language. */
export function getBlogPosts(): LocalizedBlogPost[] {
  return localize()
}

/** A single post by slug, in the active language (or undefined). */
export function getBlogPost(slug: string): LocalizedBlogPost | undefined {
  return localize().find((p) => p.slug === slug)
}

/** Distinct tags across all posts, preserving first-seen order. */
export function getBlogTags(): string[] {
  const seen = new Set<string>()
  for (const post of blogPosts) for (const tag of post.tags) seen.add(tag)
  return Array.from(seen)
}
